import type React from "react";
import { useContext } from "react";

import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { StarHollow, StarYellow } from "assets";
import { useFeatureToggleContext } from "contexts/FeatureToggleContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { Hidden } from "shared/atoms/Hidden";
import { CurrentPatientContext } from "utils/contexts";
import { Feature } from "utils/features/types";

import { usePatientActions } from "./hooks/usePatientActions";
import { PatientHeaderInfoAddNoteButton } from "./PatientHeaderInfoAddNoteButton";
import { PatientHeaderInfoCallButton } from "./PatientHeaderInfoCallButton";
import { PatientHeaderInfoShowMoreButton } from "./PatientHeaderInfoExtraButton";

interface Props {
  toggleEndTreatment: () => void;
}

export const PatientHeaderButtonRow: React.FC<Props> = ({ toggleEndTreatment }) => {
  const { patient } = useContext(CurrentPatientContext);
  const { hasFeature } = useFeatureToggleContext();
  const { t } = useTranslation();
  const { togglePriority, handleOpenMessages } = usePatientActions();

  return (
    <Hidden type="belowTablet">
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-start"
        flexWrap="wrap"
        sx={{
          rowGap: 1,
        }}
      >
        {hasFeature(Feature.MARK_PATIENT_AS_PRIO) && (
          <BaseButton onClick={togglePriority} data-testid="mark-prio-button" variant="action">
            {patient.prio ? (
              <StarYellow style={{ width: 16, height: 16 }} />
            ) : (
              <StarHollow style={{ width: 16, height: 16 }} />
            )}
            <ButtonText>{t("patients.header_info.priority_button")}</ButtonText>
          </BaseButton>
        )}

        {hasFeature(Feature.PATIENT_MESSAGES) && (
          <BaseButton
            data-testid="open-conversation-button"
            onClick={handleOpenMessages}
            variant="action"
            icon="comments"
            size="large"
          >
            <ButtonText>{t("patients.header_info.open_chat_button")}</ButtonText>
          </BaseButton>
        )}

        {hasFeature(Feature.WRITE_PATIENT_NOTE) && <PatientHeaderInfoAddNoteButton />}

        {hasFeature(Feature.CALL_PATIENT) && <PatientHeaderInfoCallButton />}

        {hasFeature(Feature.END_TREATMENT) && (
          <PatientHeaderInfoShowMoreButton toggleEndTreatment={toggleEndTreatment} />
        )}
      </Stack>
    </Hidden>
  );
};

const ButtonText = styled.span`
  white-space: nowrap;
  font-size: 14px;
`;
